import { descriptionFor } from "../../utils/gameDescriptions";
import "./index.scss";
import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { setInitialGameData } from "../../Store/store";
import { store } from "../../Store";
import SplitTargetScreen from "../SplitTargetScreen";
import PlayScreen from "../PlayScreen";
import PlayEndlessScreen from "../PlayEndlessScreen";
import PlayAgainScreen from "../PlayAgainScreen";
import GameContainer from "../GameContainer";
import {
  redirectFromParams,
  findSquishName,
  findSingleGameData,
  getSearchParams,
  capEveryWord,
  displayGameName,
  ownUnitRows,
} from "../nonUIFuncs";
import { useNavigate, useLocation } from "react-router-dom";
import ProgressBar from "../UI/ProgressBar";
import PropTypes from "prop-types";

/**
 * Game switcher component that renders different screens based on the current game state.
 * Manages transitions between target selection, play instructions, game execution, and results.
 *
 * @param {Object} props - Component props
 * @param {string} props.screen - Current screen to display
 * @param {Function} props.setScreen - Function to change the current screen
 * @param {string} props.gameDescription - Description of the current game
 * @param {Function} props.setGameTime - Function to set game time
 * @param {number} props.gameTime - Current game time
 * @param {Function} props.updateLevelData - Function to update level progression data
 * @param {Function} props.updateScoreData - Function to update score data
 * @param {Array} props.scoreData - Array of player score data
 * @param {Function} props.setProgress - Function to update game progress
 * @param {string} props.scoreType - Type of scoring system used
 * @param {string} props.gameTypeImage - Image for the game type
 * @param {Function} props.findStars - Function to calculate star ratings
 * @param {Object} props.starsObj - Object containing star calculation parameters
 * @param {Object} props.game - Game configuration object
 * @param {number} props.bestScore - Player's best score for this game
 * @param {Object} props.gameToSave - Game data to save
 * @param {number} props.starsToAdd - Number of stars to add
 * @param {string} props.gameStyle - Visual style of the game
 * @param {Object} props.config - Application configuration object
 * @param {boolean} props.splitCat - Whether categories are split
 * @param {Function} props.setSplitCat - Function to set category split state
 * @returns {JSX.Element} The appropriate screen component based on current state
 */
const GameSwitcher = ({
  screen,
  setScreen,
  gameDescription,
  setGameTime,
  gameTime,
  updateLevelData,
  updateScoreData,
  scoreData,
  setProgress,
  scoreType,
  gameTypeImage,
  findStars,
  starsObj,
  game,
  bestScore,
  gameToSave,
  starsToAdd,
  gameStyle,
  config,
  splitCat,
  setSplitCat,
  dataset,
}) => {
  switch (screen) {
    case "splitTargetScreen":
      return (
        <SplitTargetScreen setScreen={setScreen} setSplitCat={setSplitCat} />
      );
    case "playScreen":
      return (
        <PlayScreen
          setScreen={setScreen}
          gameDescription={gameDescription}
          scoreData={scoreData}
          gameTypeImage={gameTypeImage}
          config={config}
        />
      );
    case "gameScreen":
      return (
        <GameContainer
          splitCat={splitCat}
          gameTime={gameTime}
          setScreen={setScreen}
          setGameTime={setGameTime}
          updateLevelData={updateLevelData}
          setProgress={setProgress}
          scoreType={scoreType}
          gameTypeImage={gameTypeImage}
          findStars={findStars}
          starsObj={starsObj}
          gameStyle={gameStyle}
          dataset={dataset}
        />
      );
    case "playAgainScreen":
      return (
        <PlayAgainScreen
          setScreen={setScreen}
          gameTime={gameTime}
          updateScoreData={updateScoreData}
          scoreType={scoreType}
          gameTypeImage={gameTypeImage}
          starsObj={starsObj}
          game={game}
          bestScore={bestScore}
          gameToSave={gameToSave}
          starsToAdd={starsToAdd}
        />
      );
    case "playEndlessScreen":
      return (
        <PlayEndlessScreen
          setScreen={setScreen}
          gameDescription={gameDescription}
          scoreData={scoreData}
          gameTypeImage={gameTypeImage}
        />
      );
    default:
      return (
        <PlayScreen
          setScreen={setScreen}
          gameDescription={gameDescription}
          scoreData={scoreData}
          gameTypeImage={gameTypeImage}
        />
      );
  }
};

/**
 * Full game container component that manages the complete game lifecycle.
 * Handles game initialization, screen transitions, scoring, and data persistence.
 * Serves as the main wrapper for all game-related functionality.
 *
 * @param {Object} props - Component props
 * @param {Function} props.setShowTopNav - Function to control top navigation visibility
 * @param {Function} props.updateLevelData - Function to update level progression data
 * @param {Function} props.updateScoreData - Function to update score data
 * @param {Array} props.scoreData - Array of player score data
 * @param {string} props.dataset - Current dataset identifier
 * @param {Object} props.config - Application configuration object
 * @returns {JSX.Element} Complete game container with state management
 */
const FullGameContainer = ({
  setShowTopNav,
  updateLevelData,
  updateScoreData,
  scoreData,
  config,
  dataset,
}) => {
  const dispatch = useDispatch();
  const Location = useLocation();
  const navigate = useNavigate();

  const Params = getSearchParams(Location);
  const gameTypeParam = Params.find((p) => p.id === "game-type")?.value;
  const SquishName = findSquishName(gameTypeParam);
  const gameNameParam = Params.find((p) => p.id === "game-name")?.value;
  const unitParam = Params.find((p) => p.id === "unit")?.value;
  const difficultyParam = Params.find((p) => p.id === "difficulty");
  const keyParam = Params.find((p) => p.id === "Key");
  const locationParam = Params.find((p) => p.id === "location")?.value;
  const gameTimeRef = useRef(0);
  const gameStyleRef = useRef("normal");
  // Endless mode's URL has no unit, so unitParam is undefined there. Reading
  // .toUpperCase() off it crashed every Endless game from 2025-10-30 (1ed2dbb)
  // until 2026-10-22; with no unit, Endless uses the main vocab, as it did
  // before that commit.
  const isStoryUnit = Boolean(unitParam?.toUpperCase().includes("STORY"));
  const dataToFilter = isStoryUnit
    ? store.getState().store.value.storybookVocab
    : locationParam === "Minto"
      ? store.getState().store.value.b_vocabData
      : store.getState().store.value.vocabData;
  const dataToFilterRef = useRef(dataToFilter);
  const [screen, setScreen] = useState(
    // Endless has no unit to split, so it plays all of the chosen categories
    Location.pathname.includes("/endless-mode/")
      ? "playEndlessScreen"
      : gameTypeParam === "From Me To You"
        ? "splitTargetScreen"
        : "playScreen",
  );
  const [gameTime, setGameTime] = useState(0); // This may need a ref to get correct time in playAgainScreen
  const [progress, setProgress] = useState("0%");
  const [gameTypeImage, setGameTypeImage] = useState("");
  const [starOne, setStarOne] = useState(false);
  const [starTwo, setStarTwo] = useState(false);
  const [starThree, setStarThree] = useState(false);
  const [game, setGame] = useState({});
  const [gameStyle, setGameStyle] = useState("normal");
  const [gameToSave, setGameToSave] = useState({});
  const [starsToAdd, setStarsToAdd] = useState(0);
  const [bestScore, setBestScore] = useState("");
  const [splitCat, setSplitCat] = useState("");

  gameTimeRef.current = gameTime;
  gameStyleRef.current = gameStyle;
  dataToFilterRef.current = dataToFilter;
  useEffect(() => {
    let gameImg = gameTypeParam.replaceAll("-", "_").replaceAll(" ", "_");
    setGameTypeImage(`Game_Type_${gameImg}2x.png`);
    if (!Location.pathname.includes("endless-mode")) {
      let string = `${unitParam} games - ${displayGameName(gameTypeParam)} - ${gameNameParam}`;
      document.title = capEveryWord(string);
      let redirectPath =
        redirectFromParams(["difficulty", "unit", "game-type", "game-name"]) ||
        "";
      if (redirectPath.length > 0) {
        navigate(redirectPath);
      }
      // getGameData();
    } else {
      let string = `endless - ${displayGameName(gameTypeParam)}`;
      document.title = capEveryWord(string);
      // getEndlessData();
    }
    return () => {
      setShowTopNav(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    setShowTopNav(
      screen === "playScreen" || screen === "splitTargetScreen" ? true : false,
    );
    // Load the game's words when its start screen shows (From Me To You's
    // only after a category is picked on splitTargetScreen). Endless starts
    // on its own playEndlessScreen and never shows playScreen, so between
    // 2025-08-08 (679fca4) and 2026-10-22 it never loaded any words.
    if (screen === "playScreen" && !Location.pathname.includes("endless-mode")) {
      getGameData();
    } else if (screen === "playEndlessScreen") {
      getEndlessData();
    }
  }, [screen]);
  const getGameData = () => {
    let filteredArray = [];
    let i = 0;
    const findGame = () => {
      if (keyParam !== undefined && keyParam.value.length > 0) {
        return store
          .getState()
          .store.value.gameData.find(
            (d) => `${d.Key}${gameTypeParam}` === keyParam.value,
          );
      }
      let searchParam = config.ParamsToFindGame[i];
      let arrayToFilterFrom =
        filteredArray.length === 0 ? dataToFilterRef.current : filteredArray;
      filteredArray = [];
      arrayToFilterFrom.forEach((d) => {
        if (
          d[searchParam.data] ===
          Params.find((p) => p.id === searchParam.param)?.value
        ) {
          filteredArray.push(d);
        }
      });
      if (i < config.ParamsToFindGame.length - 1) {
        i = i + 1;
        findGame();
      }
      return filteredArray[0];
    };
    let selectedGame = findGame();
    let singleGameData = ownUnitRows(
      findSingleGameData(
        selectedGame.target,
        dataToFilterRef.current,
        locationParam,
        config.dialectStructure,
      ),
      selectedGame.category,
    );
    if (gameTypeParam === "From Me To You") {
      singleGameData = singleGameData.filter((d) => d.VTA_Cat === splitCat);
    }
    let rankFilteredData = [...singleGameData];
    if (rankFilteredData[0].rank !== undefined) {
      rankFilteredData = rankFilteredData.filter(
        (d) => Number(d.rank) <= Number(difficultyParam.value),
      );
    }
    if (rankFilteredData.length >= 8) {
      singleGameData = [...rankFilteredData];
    }
    let shortGameType = gameTypeParam
      .replaceAll(" ", "")
      .replaceAll("-", "")
      .toUpperCase();
    // Counting, Item Fall and Stepping Stones paint their own full-screen scene, under the frame's UI
    if (["COUNTING", "ITEMFALL", "VERBACTIVITY"].includes(shortGameType)) {
      setGameStyle("scene");
    }
    if (isStoryUnit) {
      let dSearchKey = "subCat";
      let gameSearch = selectedGame.target;
      if (shortGameType === "FILLINTHEBLANK") {
        setGameStyle("blank");
      } else if (shortGameType === "STORYBOOK") {
        setGameStyle("blank");
        dSearchKey = "homeName";
        gameSearch = selectedGame.category;
      }
      singleGameData = dataToFilterRef.current.filter(
        (d) => d[dSearchKey] === gameSearch,
      );
    }
    dispatch(
      setInitialGameData(
        Location,
        store.getState().store.value.gameData,
        dataToFilterRef.current,
        gameTypeParam || "Word To Image",
        singleGameData,
        false,
        undefined,
      ),
    );
  };
  const getEndlessData = () => {
    const findGameData = () => {
      let categories = Params.find((d) => d.id === "categories").value.split(
        ",",
      );
      let data = [];
      // Endless can mix units and stories ("Story 1"), and story words live
      // in their own sheet, so look in both.
      const endlessVocab = [
        ...dataToFilterRef.current,
        ...(store.getState().store.value.storybookVocab || []),
      ];
      categories.forEach((d) => {
        let dataToPush = [];
        dataToPush = endlessVocab.filter((datum) => datum.homeName === d);
        data = [...data, ...dataToPush];
      });
      return data;
    };
    // The game row Endless plays "as": one from the chosen categories that
    // offers this game, so per-unit settings are real (Item Fall's basket
    // picture comes from its row's `items`). It used to be gameData[0], the
    // sheet's first row (a story with no items), which crashed Item Fall.
    const { gameData } = store.getState().store.value;
    const chosen = Params.find((d) => d.id === "categories").value.split(",");
    const offersGame = (g) =>
      g[gameTypeParam] !== null && g[gameTypeParam] !== undefined;
    let selectedGame =
      gameData.find((g) => chosen.includes(g.category) && offersGame(g)) ??
      gameData.find(offersGame) ??
      gameData[0];
    let vocabData = findGameData();
    if (["COUNTING", "ITEM FALL", "VERB ACTIVITY"].includes(gameTypeParam.toUpperCase())) {
      setGameStyle("scene");
    }
    dispatch(
      setInitialGameData(
        Location,
        store.getState().store.value.gameData,
        dataToFilterRef.current,
        gameTypeParam || "Word To Image",
        vocabData,
        true,
        selectedGame,
      ),
    );
  };
  const findStars = (time, isStories) => {
    if (isStories) {
      let object = {
        difficulty: store.getState().store.value.difficulty,
        score: store.getState().store.value.stats.score,
        time: time,
        correct: store.getState().store.value.stats.hits,
        incorrect: store.getState().store.value.stats.misses,
        Key: store.getState().store.value.specificGame.Key,
        stars: 9,
        firstStars: 9,
        firstNextGoals: null,
        secondStars: null,
        secondNextGoals: null,
      };
      setStarOne(true);
      setStarTwo(true);
      setStarThree(true);
      setGameToSave(object);
      setStarsToAdd(object.stars - game.stars);
      return;
    } else {
      setGameTime(time);
      let gameInfo = store
        .getState()
        .store.value.goldStar.find((d) => d.game === SquishName);
      let difficultyGoldStar = {
        oneStar: gameInfo?.oneStarEasy,
        twoStar: gameInfo?.twoStarEasy,
        threeStar: gameInfo?.threeStarEasy,
        oneStarTwo: gameInfo?.oneStarTwoEasy,
        twoStarTwo: gameInfo?.twoStarTwoEasy,
        threeStarTwo: gameInfo?.threeStarTwoEasy,
      };
      if (store.getState().store.value.difficulty === "medium") {
        difficultyGoldStar = {
          oneStar: gameInfo?.oneStarMedium,
          twoStar: gameInfo?.twoStarMedium,
          threeStar: gameInfo?.threeStarMedium,
          oneStarTwo: gameInfo?.oneStarTwoMedium,
          twoStarTwo: gameInfo?.twoStarTwoMedium,
          threeStarTwo: gameInfo?.threeStarTwoMedium,
        };
      } else if (store.getState().store.value.difficulty === "hard") {
        difficultyGoldStar = {
          oneStar: gameInfo?.oneStarHard,
          twoStar: gameInfo?.twoStarHard,
          threeStar: gameInfo?.threeStarHard,
          oneStarTwo: gameInfo?.oneStarTwoHard,
          twoStarTwo: gameInfo?.twoStarTwoHard,
          threeStarTwo: gameInfo?.threeStarTwoHard,
        };
      }
      gameInfo = {
        game: gameInfo?.game,
        Key: gameInfo?.Key,
        notImplimented: gameInfo?.notImplimented,
        scoreType: gameInfo?.scoreType,
        ratings: [
          {
            type: gameInfo?.type,
            threeStar: Number(difficultyGoldStar.threeStar),
            twoStar: Number(difficultyGoldStar.twoStar),
            oneStar: Number(difficultyGoldStar.oneStar),
            stars: 0,
            nextGoals: null,
          },
          {
            type: gameInfo?.secondType,
            threeStar: Number(difficultyGoldStar.threeStarTwo),
            twoStar: Number(difficultyGoldStar.twoStarTwo),
            oneStar: Number(difficultyGoldStar.oneStarTwo),
            stars: 0,
            nextGoals: null,
          },
        ],
      };
      gameInfo.ratings = gameInfo?.ratings.filter((d) => d.type !== "none");
      let correctedTime = time === null ? 99999 : time;
      const checkFuncObj = {
        correct: (val) => store.getState().store.value.stats.score >= val,
        time: (val) => correctedTime <= val,
        incorrect: (val) => store.getState().store.value.stats.misses <= val,
        accuracy: (val) =>
          store.getState().store.value.stats.hits /
            (store.getState().store.value.stats.hits +
              store.getState().store.value.stats.misses) >=
          val,
      };
      gameInfo.ratings.forEach((d) => {
        let checkFunc = checkFuncObj[d.type];
        if (checkFunc(d.threeStar)) {
          d.stars = 3;
        } else if (checkFunc(d.twoStar)) {
          d.stars = 2;
          d.nextGoals = d.threeStar;
        } else if (checkFunc(d.oneStar)) {
          d.stars = 1;
          d.nextGoals = d.twoStar;
        } else {
          d.nextGoals = d.oneStar;
        }
      });
      let object = {
        difficulty: store.getState().store.value.difficulty,
        score: store.getState().store.value.stats.score,
        time: time,
        correct: store.getState().store.value.stats.hits,
        incorrect: store.getState().store.value.stats.misses,
        Key: store.getState().store.value.specificGame.Key,
        stars: gameInfo.ratings[0].stars,
        firstStars: gameInfo.ratings[0].stars,
        firstNextGoals: gameInfo.ratings[0].nextGoals,
        secondStars: null,
        secondNextGoals: null,
      };
      let fullGame = scoreData.find((d) => d.Key === keyParam.value);
      setGame(fullGame.difficultyScores[object.difficulty]);
      if (gameInfo.ratings.length > 1) {
        if (fullGame.secondType !== "none") {
          object.stars = gameInfo.ratings[0].stars + gameInfo.ratings[1].stars;
          object.secondStars = gameInfo.ratings[1].stars;
          object.secondNextGoals = gameInfo.ratings[1].nextGoals;
        }
      }
      setBestScore(game.score > object.score ? "previous" : "current");
      let starNum = Math.floor(
        object.stars /
          (fullGame.secondType === undefined || fullGame.secondType === "none"
            ? 1
            : 2),
      );
      if (starNum >= 1) {
        setStarOne(true);
      }
      if (starNum >= 2) {
        setStarTwo(true);
      }
      if (starNum >= 3) {
        setStarThree(true);
      }
      setGameToSave({ ...object, gameType: gameTypeParam });
      setStarsToAdd(object.stars - game.stars);
      if (starsToAdd < 0) {
        setStarsToAdd(0);
        setGameToSave(game);
      }
    }
  };
  let fullScreen = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  };
  const gameDesc = descriptionFor(gameTypeParam, store.getState().store.value.gameDescriptions);
  let scoreType = store
    .getState()
    .store.value.goldStar.find((gs) => gs.game === SquishName)?.scoreType;
  return (
    <div className="fullgamecontainer_container" style={fullScreen}>
      <GameSwitcher
        splitCat={splitCat}
        setSplitCat={setSplitCat}
        updateScoreData={updateScoreData}
        screen={screen}
        setScreen={setScreen}
        gameTime={gameTimeRef.current}
        setGameTime={setGameTime}
        gameDescription={gameDesc}
        updateLevelData={updateLevelData}
        scoreData={scoreData}
        setProgress={setProgress}
        scoreType={scoreType}
        gameTypeImage={gameTypeImage}
        findStars={findStars}
        starsObj={{ starOne: starOne, starTwo: starTwo, starThree: starThree }}
        game={game}
        bestScore={bestScore}
        gameToSave={gameToSave}
        starsToAdd={starsToAdd}
        gameStyle={gameStyleRef.current}
        config={config}
        dataset={dataset}
      />
      {/* Progress Bar / Timer */}
      {screen === "gameScreen" ? (
        <div className="gamecontainer_progressbar_container">
          <ProgressBar
            width={
              store.getState().store.value.height > 500
                ? "clamp(200px, 25vw, 350px)"
                : "clamp(100px, 12.5vw, 175px)"
            }
            id={`game-screen`}
            progress={progress}
            isTimer={scoreType === "normal" ? true : false}
          />
        </div>
      ) : null}
    </div>
  );
};

export default FullGameContainer;

GameSwitcher.propTypes = {
  screen: PropTypes.string,
  setScreen: PropTypes.func,
  gameDescription: PropTypes.string,
  setGameTime: PropTypes.func,
  gameTime: PropTypes.number,
  updateLevelData: PropTypes.func,
  updateScoreData: PropTypes.func,
  scoreData: PropTypes.array,
  setProgress: PropTypes.func,
  scoreType: PropTypes.string,
  gameTypeImage: PropTypes.string,
  findStars: PropTypes.func,
  starsObj: PropTypes.object,
  game: PropTypes.object,
  bestScore: PropTypes.string,
  gameToSave: PropTypes.object,
  starsToAdd: PropTypes.number,
  dataset: PropTypes.string,
};

FullGameContainer.propTypes = {
  setShowTopNav: PropTypes.func,
  updateLevelData: PropTypes.func,
  updateScoreData: PropTypes.func,
  scoreData: PropTypes.array,
  config: PropTypes.object,
  dataset: PropTypes.string,
};
